import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CustomerPortalLayout from './layouts/CustomerPortalLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { TenantGate } from './components/TenantGate';
import { ToastContainer } from '@invoiceflow/ui';
import { PortalProvider } from '@invoiceflow/portal-core';
import { webAuthPort, webConfigPort } from './services/adapters';
import { getActiveTenantId, useAuthStore } from './stores/useAuthStore';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import { useThemeBootstrap } from './hooks/useAppliedTheme';

// Lazy pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const InvoiceListPage = lazy(() => import('./pages/InvoiceListPage'));
const InvoiceDetailsPage = lazy(() => import('./pages/InvoiceDetailsPage'));
const InvoiceCommunicationPage = lazy(() => import('./pages/InvoiceCommunicationPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

const AppLayout = () => (
  <TenantGate>
    <CustomerPortalLayout>
      <Outlet />
    </CustomerPortalLayout>
  </TenantGate>
);

/**
 * The homepage belongs to whoever arrives: the dashboard for a signed-in
 * client, the landing page for everyone else. Nobody is bounced to /login —
 * signing in is offered, never forced. Deep links (an invoice URL from an
 * email) still go through ProtectedRoute, which remembers the destination
 * and returns to it after sign-in.
 */
const HomeRoute = () => {
  const user = useAuthStore(state => state.user);
  const authReady = useAuthStore(state => state.authReady);

  if (authReady && !user) return <LandingPage />;

  return (
    <ProtectedRoute>
      <TenantGate>
        <CustomerPortalLayout>
          <DashboardPage />
        </CustomerPortalLayout>
      </TenantGate>
    </ProtectedRoute>
  );
};

/**
 * Everything that needs the portal API lives here, inside the provider.
 * useAuthBootstrap calls usePortalApi, so running it in App — outside the
 * provider — would throw at first render.
 */
function PortalRoutes() {
  // Must run before any protected route renders.
  useAuthBootstrap();
  // Reads the stored light/dark choice and tracks the OS setting.
  useThemeBootstrap();

  return (
    <>
      <ToastContainer />
      <Router>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-brand-line border-t-brand rounded-full animate-spin"></div>
            </div>
          }
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/" element={<HomeRoute />} />

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/invoices" element={<InvoiceListPage />} />
              <Route path="/invoices/:id" element={<InvoiceDetailsPage />} />
              <Route path="/invoices/:id/communications" element={<InvoiceCommunicationPage />} />
              <Route path="/payments" element={<PaymentHistoryPage />} />
            </Route>

            {/* Unknown paths go home — outside the auth gate, so a signed-out
                visitor gets the landing page, not a login wall. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PortalProvider
        config={webConfigPort}
        auth={webAuthPort}
        getActiveTenantId={getActiveTenantId}
        onUnauthorized={() => {
          useAuthStore.getState().logout();
          // A 401 means the session is gone; cached data must not outlive it.
          queryClient.clear();
        }}
      >
        <PortalRoutes />
      </PortalProvider>
    </QueryClientProvider>
  );
}

export default App;
